import React, { useState, useEffect } from 'react';
import { Container, Box, FormControl, InputLabel, Select, MenuItem, Typography } from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import analyticsService from '../services/analyticsService';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const AnalyticsPage = () => {
  const [range, setRange] = useState(7); // default to 7 days
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    fetchData(range);
    // eslint-disable-next-line
  }, [range]);

  // Convert "range" in days to startTime & endTime
  const fetchData = async (days) => {
    const endTime = new Date();
    const startTime = new Date();
    startTime.setDate(endTime.getDate() - days);

    try {
      const results = await analyticsService.getSesMetricsByRange(startTime, endTime);
      const data = transformMetricsToChartData(results);
      setChartData(data);
    } catch (err) {
      console.error('Error fetching analytics data:', err);
    }
  };

  /**
   * Transform the MetricDataResults into a structure suitable for Chart.js.
   * We'll gather timestamps from the first metric and line them up with each metric's values.
   */
  const transformMetricsToChartData = (metricDataResults) => {
    const sendResult = metricDataResults.find((m) => m.Id === 'sendData');
    const deliveryResult = metricDataResults.find((m) => m.Id === 'deliveryData');
    const bounceResult = metricDataResults.find((m) => m.Id === 'bounceData');
    const complaintResult = metricDataResults.find((m) => m.Id === 'complaintData');

    const labels = sendResult?.Timestamps.map((ts) => {
      const dateObj = new Date(ts);
      return dateObj.toLocaleString();
    }) || [];

    const getValues = (res) => res?.Values.map((val) => val) || [];

    return {
      labels,
      datasets: [
        {
          label: 'Send',
          data: getValues(sendResult),
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
        },
        {
          label: 'Delivery',
          data: getValues(deliveryResult),
          borderColor: 'rgb(54, 162, 235)',
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
        },
        {
          label: 'Bounce',
          data: getValues(bounceResult),
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
        },
        {
          label: 'Complaint',
          data: getValues(complaintResult),
          borderColor: 'rgb(255, 159, 64)',
          backgroundColor: 'rgba(255, 159, 64, 0.2)',
        },
      ],
    };
  };

  // Chart options with white text for all elements
  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#fff', // Legend labels in white
        },
      },
      title: {
        display: true,
        text: 'SES Metrics Over Time',
        color: '#fff', // Title in white
      },
      tooltip: {
        titleColor: '#fff',
        bodyColor: '#fff',
        footerColor: '#fff',
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#fff', // x-axis labels in white
        },
        grid: {
          color: 'rgba(255,255,255,0.1)',
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: '#fff', // y-axis labels in white
        },
        grid: {
          color: 'rgba(255,255,255,0.1)',
        },
      },
    },
  };

  return (
    <Container
      sx={{
        mt: 4,
        backgroundColor: '#000',
        color: '#fff',
        p: 2,
        borderRadius: 2,
      }}
    >
      <Typography variant="h5" gutterBottom sx={{ color: '#fff' }}>
        Advanced SES Analytics (CloudWatch)
      </Typography>

      {/* Range Picker */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <FormControl size="small">
          <InputLabel id="range-label" sx={{ color: '#fff' }}>
            Date Range
          </InputLabel>
          <Select
            labelId="range-label"
            value={range}
            label="Date Range"
            onChange={(e) => setRange(e.target.value)}
            sx={{
              width: 120,
              backgroundColor: '#1E1E1E',
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                backgroundColor: '#1E1E1E',
                '& fieldset': {
                  borderColor: '#fff',
                },
                '&:hover fieldset': {
                  borderColor: '#fff',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#fff',
                },
              },
              '& .MuiSvgIcon-root': {
                color: '#fff',
              },
            }}
          >
            <MenuItem value={7}>Last 7 days</MenuItem>
            <MenuItem value={14}>Last 14 days</MenuItem>
            <MenuItem value={30}>Last 30 days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {chartData ? (
        <Box sx={{ height: 500 }}>
          <Line data={chartData} options={options} />
        </Box>
      ) : (
        <Typography>Loading data...</Typography>
      )}
    </Container>
  );
};

export default AnalyticsPage;
